import { useState } from "react";
import { Sparkles } from "lucide-react";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Cost, CopyButton, PageHeader, Pills, Section, Spinner } from "../components/AiHtml";
import { useGenerate } from "../lib/useGenerate";

const toText = (r) => [r.title, r.meta_description, ...(r.sections || []).flatMap((s) => [s.heading, ...s.paragraphs]), ...(r.faq || []).flatMap((f) => [f.q, f.a])].join("\n\n");

const Longform = ({ r }) => (
  <Section title={r.title} right={<div className="flex items-center gap-2"><span className="badge-o">{r.word_count || toText(r).split(/\s+/).length} words</span><CopyButton testId="content-copy-btn" text={toText(r)} /></div>} testId="content-result">
    <p className="text-sm text-slate-400 italic mb-6">{r.meta_description}</p>
    <article className="space-y-6 max-w-3xl">
      {(r.sections || []).map((s, i) => (
        <div key={i}><h3 className="text-xl font-semibold text-slate-50 mb-2">{s.heading}</h3>{s.paragraphs.map((p, j) => <p key={j} className="text-[15px] text-slate-300 leading-relaxed mb-3">{p}</p>)}</div>
      ))}
      {r.faq?.length > 0 && (
        <div className="border-t border-slate-800 pt-5"><p className="label-mono mb-3">FAQ</p>
          {r.faq.map((f, i) => <div key={i} className="mb-3"><p className="font-medium text-slate-100">{f.q}</p><p className="text-sm text-slate-400">{f.a}</p></div>)}
        </div>
      )}
    </article>
  </Section>
);

const Social = ({ r }) => (
  <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="content-result">
    {(r.posts || []).map((p, i) => (
      <Section key={i} title={p.platform} right={<CopyButton text={`${p.hook}\n\n${p.caption}\n\n${(p.hashtags || []).join(" ")}`} />}>
        <p className="text-sm font-semibold text-orange-300 mb-2">{p.hook}</p>
        <p className="text-sm text-slate-300 whitespace-pre-line">{p.caption}</p>
        <p className="text-xs text-blue-400 mt-3">{(p.hashtags || []).map((h) => (h.startsWith("#") ? h : `#${h}`)).join(" ")}</p>
      </Section>
    ))}
  </div>
);

export default function ContentStudio() {
  const [type, setType] = useState("blog");
  const [tone, setTone] = useState("Professional");
  const [prompt, setPrompt] = useState("");
  const g = useGenerate(type);
  return (
    <div className="space-y-6">
      <PageHeader label="Content Studio" title="Blogs, articles and social bundles" desc="SEO-ready long-form and platform-native social posts, delivered as clean formatted cards." />
      <div className="card-n p-5 space-y-4">
        <div className="flex flex-wrap justify-between gap-3">
          <Pills testPrefix="content-type" value={type} onChange={(t) => { setType(t); g.setResult(null); }} options={[{ value: "blog", label: "Blog" }, { value: "article", label: "Article" }, { value: "social", label: "Social Bundle" }]} />
          <Cost n={10} />
        </div>
        <Pills testPrefix="content-tone" value={tone} onChange={setTone} options={["Professional", "Friendly", "Bold", "Witty", "Hinglish"]} />
        <Textarea data-testid="content-prompt-input" rows={3} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Topic, target keyword, audience..." />
        <Button data-testid="content-generate-btn" disabled={g.busy || !prompt.trim()} onClick={() => g.run(prompt, { tone }, type)} className="rounded-full"><Sparkles size={16} className="mr-1.5" /> Generate</Button>
      </div>
      {g.busy && <div className="card-n"><Spinner label="Writing..." /></div>}
      {!g.busy && g.result && (type === "social" ? <Social r={g.result} /> : <Longform r={g.result} />)}
    </div>
  );
}
